import React, { useCallback, useEffect, useMemo, useState } from "react";
import AppShell from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Download, Search, Pill, Bell, Play } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

export default function AdminDispenses() {
  const [rows, setRows] = useState([]);
  const [kpi, setKpi] = useState({ total: 0, today: 0, last_7d: 0, last_30d: 0, unique_pharmacies: 0 });
  const [filters, setFilters] = useState({ pharmacy: "", doctor: "", date_from: "", date_to: "" });
  const [reminders, setReminders] = useState([]);
  const [triggering, setTriggering] = useState(false);

  const load = useCallback(async () => {
    try {
      const params = Object.fromEntries(
        Object.entries(filters).filter(([_, v]) => v && v.trim())
      );
      const [{ data: lead }, { data: rem }] = await Promise.all([
        api.get("/admin/clinic-growth/dispenses", { params }),
        api.get("/admin/clinic-growth/refill-reminders"),
      ]);
      setRows(lead.dispenses || []);
      setKpi(lead.kpi || {});
      setReminders(rem.reminders || []);
    } catch (e) { toast.error(formatApiError(e)); }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  const exportCsv = () => {
    const params = new URLSearchParams(
      Object.entries(filters).filter(([_, v]) => v && v.trim())
    );
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/clinic-growth/dispenses.csv?${params}`;
    window.open(url, "_blank");
  };

  const triggerReminder = async () => {
    setTriggering(true);
    try {
      const { data } = await api.post("/admin/clinic-growth/refill-reminder/run");
      toast.success(`Refill tick: ${data.summary.due} due, ${data.summary.sent} sent`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setTriggering(false); }
  };

  return (
    <AppShell
      pageTitle="Dispense ledger"
      pageDescription="Every pharmacy attestation across ClinicGrowth e-prescriptions, filterable and auditable."
    >
      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="admin-dispense-kpis">
        <KpiPill label="Total" value={kpi.total} />
        <KpiPill label="Today" value={kpi.today} tone="emerald" />
        <KpiPill label="Last 7 days" value={kpi.last_7d} tone="sky" />
        <KpiPill label="Last 30 days" value={kpi.last_30d} tone="lavender" />
        <KpiPill label="Pharmacies" value={kpi.unique_pharmacies} tone="amber" />
      </div>

      {/* Filters + actions */}
      <div className="mt-6 grid grid-cols-1 md:grid-cols-5 gap-3 items-end">
        <div className="md:col-span-1">
          <Label>Pharmacy</Label>
          <div className="relative mt-1">
            <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input value={filters.pharmacy}
                   onChange={(e) => setFilters({ ...filters, pharmacy: e.target.value })}
                   placeholder="Apollo"
                   className="pl-7" data-testid="admin-dispense-pharmacy" />
          </div>
        </div>
        <div className="md:col-span-1">
          <Label>Doctor / RMP #</Label>
          <Input value={filters.doctor}
                 onChange={(e) => setFilters({ ...filters, doctor: e.target.value })}
                 placeholder="Dr Priya or MCI-47891"
                 className="mt-1" data-testid="admin-dispense-doctor" />
        </div>
        <div><Label>From</Label><Input type="date" value={filters.date_from} onChange={(e) => setFilters({ ...filters, date_from: e.target.value })} className="mt-1" data-testid="admin-dispense-from" /></div>
        <div><Label>To</Label><Input type="date" value={filters.date_to} onChange={(e) => setFilters({ ...filters, date_to: e.target.value })} className="mt-1" data-testid="admin-dispense-to" /></div>
        <div className="flex gap-2 justify-end">
          <Button variant="outline" onClick={exportCsv} data-testid="admin-dispense-csv">
            <Download size={14} className="mr-1" /> CSV
          </Button>
        </div>
      </div>

      {/* Table */}
      <div className="mt-4 rounded-lg border border-border bg-white overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Dispensed at</TableHead>
              <TableHead>Pharmacy · Pharmacist</TableHead>
              <TableHead>Patient</TableHead>
              <TableHead>Doctor · RMP</TableHead>
              <TableHead>Diagnosis</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow><TableCell colSpan={5} className="text-center text-sm text-muted-foreground py-10">No dispenses match this filter.</TableCell></TableRow>
            ) : rows.map((r) => (
              <TableRow key={r.dispense_id} data-testid={`admin-dispense-row-${r.dispense_id}`}>
                <TableCell className="text-sm whitespace-nowrap">
                  {new Date(r.dispensed_at).toLocaleString(undefined, { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                </TableCell>
                <TableCell className="text-sm">
                  <div className="text-primary flex items-center gap-1"><Pill size={12} /> {r.pharmacy_name}</div>
                  {r.pharmacist_name && <div className="text-[11px] text-muted-foreground">by {r.pharmacist_name}</div>}
                </TableCell>
                <TableCell className="text-sm text-primary">{r.patient_name_masked}</TableCell>
                <TableCell className="text-sm">
                  <div className="text-primary">{r.doctor_name}</div>
                  {r.rmp_number && <div className="font-mono text-[11px] text-muted-foreground">{r.rmp_number}</div>}
                </TableCell>
                <TableCell className="text-sm text-muted-foreground">{r.diagnosis || "—"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Refill reminders audit */}
      <div className="mt-10 rounded-lg border border-border bg-white p-6" data-testid="admin-refill-reminders">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <Bell size={14} className="text-primary" />
            <div>
              <div className="overline">Smart refill reminder</div>
              <div className="font-serif text-xl text-primary">
                Scheduled 10:00 IST · {LEAD_DAYS_DESC}
              </div>
            </div>
          </div>
          <Button onClick={triggerReminder} disabled={triggering} data-testid="admin-refill-trigger">
            <Play size={14} className="mr-1" /> {triggering ? "Running…" : "Run now"}
          </Button>
        </div>
        <div className="mt-4 overflow-hidden rounded-md border border-border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Sent at</TableHead>
                <TableHead>Patient</TableHead>
                <TableHead>Doctor</TableHead>
                <TableHead>Runout</TableHead>
                <TableHead>Dispatch</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {reminders.length === 0 ? (
                <TableRow><TableCell colSpan={5} className="text-center text-xs text-muted-foreground py-6">No reminders fired yet — this fills up as patients approach their run-out dates.</TableCell></TableRow>
              ) : reminders.map((r) => (
                <TableRow key={r.reminder_id} data-testid={`admin-refill-row-${r.reminder_id}`}>
                  <TableCell className="text-sm whitespace-nowrap">{new Date(r.sent_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}</TableCell>
                  <TableCell className="text-sm text-primary">{r.patient_name_masked}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{r.doctor_name}</TableCell>
                  <TableCell className="text-sm whitespace-nowrap">
                    <span className="text-muted-foreground">{r.duration_days}d · </span>
                    {r.estimated_runout_at ? new Date(r.estimated_runout_at).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "—"}
                  </TableCell>
                  <TableCell>
                    <DispatchBadge status={r.dispatch_status} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    </AppShell>
  );
}

const LEAD_DAYS_DESC = "WhatsApp nudges 7 days before meds run out";

function KpiPill({ label, value, tone = "primary" }) {
  const tones = {
    primary: "border-primary/15 bg-primary/5 text-primary",
    emerald: "border-emerald-200 bg-emerald-50 text-emerald-800",
    sky: "border-sky-200 bg-sky-50 text-sky-800",
    lavender: "border-violet-200 bg-violet-50 text-violet-800",
    amber: "border-amber-200 bg-amber-50 text-amber-800",
  };
  return (
    <div className={`rounded-lg border p-4 ${tones[tone]}`}>
      <div className="text-[10px] uppercase tracking-widest opacity-80">{label}</div>
      <div className="mt-1 font-serif text-3xl">{value ?? 0}</div>
    </div>
  );
}

function DispatchBadge({ status }) {
  if (!status) return <Badge variant="outline">—</Badge>;
  if (status === "sent") return <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200">Sent</Badge>;
  if (status.startsWith("skipped")) return <Badge className="bg-amber-100 text-amber-800 border-amber-200">Skipped · Twilio off</Badge>;
  if (status.startsWith("failed")) return <Badge className="bg-rose-100 text-rose-800 border-rose-200">{status}</Badge>;
  return <Badge variant="outline">{status}</Badge>;
}
